import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, LayoutGrid, ShoppingCart, Truck, Wallet, Users, Boxes, ClipboardList, Landmark, Settings, Wand2, BookOpen, Upload } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { AccDashboard } from "./AccDashboard";
import { VoucherList } from "./VoucherList";
import { PartiesTab, ProductsTab } from "./AccMasters";
import { ProcurementTab, GstTab, CompanySettings } from "./AccMisc";
import { AutoInvoiceTab } from "./AutoInvoiceTab";
import { BooksTab } from "./BooksTab";
import { ImportTab } from "./ImportTab";

export const accApi = (path, opts = {}) => axios({ url: `${API}/acc${path}`, headers: getAuthHeader(), ...opts }).then(r => r.data);
export const accErr = (e) => toast.error(e?.response?.data?.detail || "Something went wrong");
export const money = (n, cur = "INR") => `${Number(n) < 0 ? "−" : ""}${cur === "USD" ? "$" : "₹"}${Math.abs(Number(n || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const THEMES = ["#4f46e5", "#0f766e", "#b45309", "#be123c", "#7c3aed", "#0369a1"];

const TABS = [
  ["dash", "Overview", LayoutGrid], ["auto", "From pending orders", Wand2], ["sale", "Sales", ShoppingCart], ["purchase", "Purchase", Truck],
  ["cash", "Receipts / Payments", Wallet], ["parties", "Debtors & Creditors", Users], ["stock", "Products & Stock", Boxes],
  ["books", "Bank, Cash & Expenses", BookOpen], ["procure", "Procurement", ClipboardList], ["gst", "GST & E-way", Landmark],
  ["import", "Import (Marg)", Upload], ["settings", "Company", Settings],
];

export default function AccountsPanel({ user }) {
  const [companies, setCompanies] = useState([]);
  const [cid, setCid] = useState(localStorage.getItem("acc_company") || "");
  const [tab, setTab] = useState("dash");
  const load = useCallback(() => accApi("/companies").then(cs => {
    setCompanies(cs);
    if (!cs.find(c => c.id === cid) && cs[0]) setCid(cs[0].id);
  }).catch(accErr), [cid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (cid) localStorage.setItem("acc_company", cid); }, [cid]);
  const company = companies.find(c => c.id === cid);
  const color = company?.theme_color || THEMES[Math.max(0, companies.indexOf(company)) % THEMES.length];
  if (!company) return <div className="p-8 text-sm text-slate-500" data-testid="accounts-panel">Loading companies…</div>;
  return (
    <div className="space-y-4" data-testid="accounts-panel" style={{ "--acc": color }}>
      <div className="rounded-2xl p-4 text-white shadow-lg flex flex-wrap items-center gap-3 max-sm:flex-col max-sm:items-stretch" style={{ background: color }} data-testid="acc-company-banner">
        <Building2 className="w-7 h-7 opacity-90" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest opacity-80">Accounts · working in</div>
          <div className="text-xl font-bold truncate" data-testid="acc-company-name">{company.name}</div>
          <div className="text-xs opacity-80">GSTIN {company.gstin || "—"} · {company.state || company.state_hi || "state not set"}{company.lut_no ? ` · LUT ${company.lut_no}` : ""}</div>
        </div>
        <select value={cid} onChange={(e) => { setCid(e.target.value); toast.success(`Switched to ${companies.find(c => c.id === e.target.value)?.name}`); }}
          className="rounded-lg bg-white/95 px-3 py-2 text-sm font-semibold text-slate-800" data-testid="acc-company-switch">
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}{c.is_default ? " (default)" : ""}</option>)}
        </select>
      </div>
      <div className="flex gap-1 overflow-x-auto rounded-xl border bg-white p-1" data-testid="acc-tabs">
        {TABS.map(([k, l, I]) => (
          <button key={k} onClick={(e) => { setTab(k); e.currentTarget.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" }); }} data-testid={`acc-tab-${k}`}
            className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${tab === k ? "text-white" : "text-slate-600 hover:bg-slate-50"}`}
            style={tab === k ? { background: color } : undefined}><I className="w-4 h-4" /> {l}</button>))}
      </div>
      {tab === "dash" && <AccDashboard company={company} />}
      {["sale", "purchase", "cash"].includes(tab) && <VoucherList key={tab + cid} kind={tab} company={company} />}
      {tab === "parties" && <PartiesTab company={company} />}
      {tab === "stock" && <ProductsTab company={company} />}
      {tab === "procure" && <ProcurementTab company={company} />}
      {tab === "auto" && <AutoInvoiceTab company={company} onDone={() => setTab("sale")} />}
      {tab === "books" && <BooksTab company={company} />}
      {tab === "import" && <ImportTab company={company} />}
      {tab === "gst" && <GstTab company={company} />}
      {tab === "settings" && <CompanySettings company={company} onSaved={load} />}
    </div>
  );
}
